const countryDisplayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  google_ads: 'Google Ads',
  yahoo_dsp: 'Yahoo DSP',
  nexxen_dsp: 'Nexxen DSP',
  google_dv360: 'DV360',
  dv_360: 'DV360',
  meta_ads: 'Meta Ads',
  facebook_ads: 'Meta Ads',
  linkedin_ads: 'LinkedIn Ads',
};

const GEO_FIELD_LABELS: Record<string, string> = {
  country_code: 'Country',
  region: 'Region',
  city: 'City',
  dma: 'DMA',
  zip: 'Zip',
  zipcode: 'Zip',
};

const DOMAIN_FIELD_LABELS: Record<string, string> = {
  domain: 'Domain',
  app: 'App',
  app_id: 'App',
  app_name: 'App Name',
  inventory_type: 'Inventory Type',
};

const LABEL_ACRONYMS = new Set(['DSP', 'DV360', 'DMA', 'ID', 'US', 'UK', 'EU', 'CTV', 'OTT']);

const isSnakeCaseToken = (value: string) => /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/i.test(value.trim());

/** Map ISO 3166-1 alpha-2 codes (e.g. US) to English country names. */
export function formatCountryCode(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return code;

  const upper = trimmed.toUpperCase();
  if (upper.length !== 2) return trimmed;

  try {
    return countryDisplayNames?.of(upper) ?? trimmed;
  } catch {
    return trimmed;
  }
}

export function formatTokenizedLabel(value: string): string {
  return value
    .trim()
    .split('_')
    .filter(Boolean)
    .map((token) => {
      const upper = token.toUpperCase();
      if (LABEL_ACRONYMS.has(upper)) return upper;
      if (upper === token && token.length <= 4 && /^[A-Z0-9]+$/.test(token)) return upper;
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
}

export function formatPlatformLabel(source: string): string {
  const key = source.trim().toLowerCase();
  return PLATFORM_DISPLAY_NAMES[key] ?? formatTokenizedLabel(source);
}

export function formatGeoFieldLabel(field: string): string {
  return GEO_FIELD_LABELS[field] ?? formatTokenizedLabel(field);
}

export function formatDomainFieldLabel(field: string): string {
  return DOMAIN_FIELD_LABELS[field] ?? formatTokenizedLabel(field);
}

export function formatGeoBreakdownValue(field: string, rawKey: string): string {
  if (field === 'country_code') {
    return formatCountryCode(rawKey);
  }
  if (isSnakeCaseToken(rawKey)) {
    return formatTokenizedLabel(rawKey);
  }
  return rawKey;
}

export function formatDomainBreakdownValue(field: string, rawKey: string): string {
  if (field === 'inventory_type' && isSnakeCaseToken(rawKey)) {
    return formatTokenizedLabel(rawKey);
  }
  return rawKey;
}

export type BreakdownKind = 'geo' | 'domain';

export function formatBreakdownValue(
  kind: BreakdownKind,
  field: string,
  rawKey: string,
): string {
  return kind === 'geo'
    ? formatGeoBreakdownValue(field, rawKey)
    : formatDomainBreakdownValue(field, rawKey);
}

export function formatBreakdownFieldLabel(kind: BreakdownKind, field: string): string {
  return kind === 'geo' ? formatGeoFieldLabel(field) : formatDomainFieldLabel(field);
}

/** @deprecated Use formatGeoBreakdownValue */
export function formatGeoLabel(field: string, rawKey: string): string {
  return formatGeoBreakdownValue(field, rawKey);
}
